import React, { FC, FormHTMLAttributes, useCallback, useMemo } from 'react';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';

import { AppsConstants } from '../store/constants';
import { priceWithMax2DecimalsRegex, t } from 'utils';
import { IAppCountry, IAppPlan } from '../store/actions';
import { savePricingInfo } from '../store/actions/index';
import AppPricingForm, { IAppPlanForm } from './AppPricingForm';

export interface IAppPricing extends FormHTMLAttributes<HTMLFormElement> {
  id: string;
}

export interface IAppCountryForm extends IAppCountry {
  onetimeFee: boolean;
  prefillCountryIndex: string;
  description: string;
  monthlyPrice: string;
  currencyCode: string;
}

const AppPricing: FC<IAppPricing> = (props) => {
  const { id } = props;
  const dispatchRequest = useDispatchRequest();
  const {
    data: { status, allowedCountries },
  } = useQuery({ type: AppsConstants.APP, requestKey: id });
  // transform allowed countries for form
  const initialAllowedCountries = useMemo<IAppCountryForm[]>(
    () =>
      allowedCountries.map((country: IAppCountry) => ({
        ...country,
        onetimeFee: !!country.activationFeePrice,
        prefillCountryIndex: 'none',
      })),
    [allowedCountries]
  );
  const { plans: initialPlans = [] } = initialAllowedCountries[0] ?? {};
  const transformedInitialPlans = initialPlans?.map((initialPlan) => ({
    ...initialPlan,
    activationFeePrice: initialAllowedCountries[0].activationFeePrice,
    onetimeFee: !!initialAllowedCountries[0].activationFeePrice,
  }));

  // Determine the pricing option
  const isPayPerUsage = initialPlans?.reduce((hasPayPerUsageFields: boolean, plan: IAppPlan) => {
    if (hasPayPerUsageFields) return true;
    if (plan.usageMaxUnitPrice || plan.usageMinUnitPrice || plan.usageUnitPrice) return true;
    return false;
  }, false);
  const isMembership = initialPlans?.reduce((hasMembershipFields: boolean, plan: IAppPlan) => {
    if (hasMembershipFields) return true;
    return !!plan.monthlyPrice;
  }, false);
  const isOnetime =
    !isMembership &&
    !isPayPerUsage &&
    initialAllowedCountries.reduce((hasOnetimeFee: boolean, country: IAppCountryForm) => {
      if (hasOnetimeFee) return true;
      if (country.activationFeePrice > 0) return true;
      return false;
    }, false);
  // const isFree = !isMembership && !isPayPerUsage && !isOnetime;
  const pricingType = isMembership
    ? 'membership'
    : isPayPerUsage
    ? 'payPerUsage'
    : isOnetime
    ? 'onetime'
    : 'free';

  // decide if pay per usage plan is with fixed pricing
  const isPayPerUsageFixed = useMemo(
    (): boolean =>
      isPayPerUsage &&
      initialAllowedCountries.reduce((isFixed: boolean, country: IAppCountryForm) => {
        if (isFixed) return true;
        const { plans }: { plans: IAppPlan[] } = country;
        const hasMaxUnitPriceInPlans = plans?.reduce((hasMaxUnitPrice: boolean, plan: IAppPlan) => {
          if (hasMaxUnitPrice) return true;
          const { usageMaxUnitPrice, usageMinUnitPrice, usageUnitPrice } = plan;
          return usageMaxUnitPrice === usageMinUnitPrice && usageMaxUnitPrice === usageUnitPrice;
        }, false);
        return hasMaxUnitPriceInPlans;
      }, false),
    [initialAllowedCountries, isPayPerUsage]
  );

  // decide if membership is with single/multiple plan
  const isMembershipMultiplePlans = useMemo(
    (): boolean =>
      isMembership &&
      initialAllowedCountries.reduce((hasMultiplePlans: boolean, country: IAppCountryForm) => {
        if (hasMultiplePlans) return true;
        const { plans }: { plans: IAppPlan[] } = country;
        return plans?.length > 1;
      }, false),
    [initialAllowedCountries, isMembership]
  );

  // One-time
  const onetimeTransform = useCallback((values: { [key: string]: any }): IAppCountry[] => {
    const { allowedCountries } = values;
    // transform allowed countries
    return allowedCountries.map((country: IAppCountryForm) => {
      const { prefillCountryIndex } = country;
      const formatValues = (countryToTransform: IAppCountryForm): any => ({
        country: country.country,
        activationFeePrice: +countryToTransform.activationFeePrice,
        priceDescription: countryToTransform.priceDescription ?? '',
        plans: countryToTransform.plans,
      });
      if (parseFloat(prefillCountryIndex) >= 0) {
        // copy values from other country
        const countryToCopyFrom = allowedCountries[parseFloat(prefillCountryIndex)];
        return formatValues(countryToCopyFrom);
      }
      return formatValues(country);
    });
  }, []);

  const onetimeValidate = useCallback((values: { [key: string]: any }): any => {
    const errors: any = {
      allowedCountries: [],
    };
    const { allowedCountries } = values;
    allowedCountries.forEach((allowedCountry: IAppCountryForm, i: number) => {
      const countryErrors: { [key: string]: string } = {};
      // this is to determine if we should validate the country that's used to prefill this one or the country itself. Regardless, we must show errors in this country.
      let indexOfCountryToValidate = i;
      const { prefillCountryIndex } = allowedCountry;
      if (+prefillCountryIndex >= 0) {
        indexOfCountryToValidate = +prefillCountryIndex;
      }
      const countryToValidate = allowedCountries[indexOfCountryToValidate];
      const { activationFeePrice, priceDescription } = countryToValidate;
      if (+activationFeePrice < 0.01) {
        countryErrors.activationFeePrice = t('errors.price.invalid');
      }
      if (!priceDescription?.length) {
        countryErrors.priceDescription = t('errors.required');
      }
      errors.allowedCountries?.push(countryErrors);
    });

    // return empty object if no errors
    const hasErrors = errors.allowedCountries.reduce(
      (hasError: boolean, countryErrObject: { [key: string]: string }) => {
        return hasError || Object.keys(countryErrObject).length > 0;
      },
      false
    );
    return hasErrors ? errors : {};
  }, []);

  // Membership
  const membershipTransform = useCallback((values) => {
    const { allowedCountries, plans } = values;
    return allowedCountries.map((country: IAppCountryForm) => {
      const { prefillCountryIndex } = country;
      const formatValues = (countryToTransform: IAppCountryForm): any => {
        return {
          country: country.country,
          activationFeePrice: plans[0]?.onetimeFee ? +plans[0]?.activationFeePrice : 0, // not correct for multiple plans, but can't be different since we only save a country once and put all plans inside
          plans: plans?.map((plan: IAppPlanForm) => ({
            name: plan.name,
            description: plan.description,
            monthlyPrice: +plan.monthlyPrice,
          })),
        };
      };
      if (parseFloat(prefillCountryIndex) >= 0) {
        // Select for other country to prefill from is active - find and copy values
        const countryToCopyFrom = allowedCountries[parseFloat(prefillCountryIndex)];
        return formatValues(countryToCopyFrom);
      }
      return formatValues(country);
    });
  }, []);

  const membershipValidate = useCallback((values) => {
    const errors: any = { plans: [] };
    const { plans } = values;
    plans.forEach((plan: IAppPlanForm) => {
      const planErrors: any = {};
      // const { name, onetimeFee, description, monthlyPrice } = plan;
      const { name, description, monthlyPrice, onetimeFee, activationFeePrice } = plan;
      if (!description?.length) {
        planErrors.description = t('errors.required');
      }
      if (+monthlyPrice < 0.01) {
        planErrors.monthlyPrice = t('errors.price.invalid');
      }
      if (!name?.length) {
        planErrors.name = t('errors.required');
      }
      if (!name?.trim()?.length) {
        planErrors.name = t('errors.planName.invalid');
      }
      // countries
      if (!!onetimeFee && !`${activationFeePrice ?? ''}`.match(priceWithMax2DecimalsRegex)) {
        planErrors.activationFeePrice = t('errors.price.invalid');
      }
      errors.plans.push(planErrors);
    });

    // return empty object if no errors
    const hasErrors = errors.plans.reduce(
      (hasError: boolean, planErrObject: { [key: string]: string }) => {
        return hasError || Object.keys(planErrObject).length > 0;
      },
      false
    );
    return hasErrors ? errors : {};
  }, []);

  // Pay-per-usage
  const payPerUsageTransform = useCallback((values) => {
    const {
      allowedCountries,
      payPerUsageType,
      copyPrices,
      copyPricesMinEUR,
      copyPricesMinUSD,
      copyPricesMaxEUR,
      copyPricesMaxUSD,
      copyPricesUnitPriceUSD,
      copyPricesUnitPriceEUR,
    } = values;
    const isVariablePayPerUsage = payPerUsageType === 'variable';
    const isFixedPayPerUsage = payPerUsageType === 'fixed';
    // transform allowed countries
    return allowedCountries.map((country: IAppCountryForm) => {
      const { prefillCountryIndex, currencyCode } = country;
      const formatValues = (countryToTransform: IAppCountryForm): any => {
        return {
          country: country.country,
          activationFeePrice: countryToTransform.onetimeFee
            ? +countryToTransform.activationFeePrice ?? 0
            : 0,
          trialUnits: +countryToTransform.trialUnits,
          unitDescription: countryToTransform.unitDescription,
          unitDescriptionPlural: countryToTransform.unitDescriptionPlural,
          priceDescription: countryToTransform.priceDescription ?? '',
          plans: countryToTransform.plans?.map((plan: IAppPlan) => {
            const result: { [key: string]: any } = {};
            // determine currency of country
            const copyPricesUnitPrice =
              currencyCode === 'EUR' ? copyPricesUnitPriceEUR : copyPricesUnitPriceUSD;
            const copyPricesMin = currencyCode === 'EUR' ? copyPricesMinEUR : copyPricesMinUSD;
            const copyPricesMax = currencyCode === 'EUR' ? copyPricesMaxEUR : copyPricesMaxUSD;
            if (isFixedPayPerUsage) {
              result.usageUnitPrice = copyPrices ? +copyPricesUnitPrice : +plan.usageUnitPrice;
            }
            if (isVariablePayPerUsage) {
              result.usageMinUnitPrice = copyPrices ? +copyPricesMin : +plan.usageMinUnitPrice;
              result.usageMaxUnitPrice = copyPrices ? +copyPricesMax : +plan.usageMaxUnitPrice;
            }
            return result;
          }),
        };
      };
      if (parseFloat(prefillCountryIndex) >= 0) {
        // Select for other country to prefill from is active - find and copy values
        const countryToCopyFrom = allowedCountries[parseFloat(prefillCountryIndex)];
        return formatValues(countryToCopyFrom);
      }
      return formatValues(country);
    });
  }, []);

  const payPerUsageValidate = useCallback((values: { [key: string]: any }): any => {
    const { copyPrices, payPerUsageType, allowedCountries, ...otherValues } = values;
    const isVariablePPU = payPerUsageType === 'variable';

    const errors: any = {
      allowedCountries: [],
    };
    allowedCountries.forEach((allowedCountry: IAppCountryForm, i: number) => {
      const countryErrors: { [key: string]: string } = {};
      // this is to determine if we should validate the country that's used to prefill this one or the country itself. Regardless, we must show errors in this country.
      let indexOfCountryToValidate = i;
      const { prefillCountryIndex } = allowedCountry;
      if (+prefillCountryIndex >= 0) {
        indexOfCountryToValidate = +prefillCountryIndex;
      }
      const countryToValidate: IAppCountryForm = allowedCountries[indexOfCountryToValidate];
      const {
        plans,
        onetimeFee,
        unitDescription,
        priceDescription,
        activationFeePrice,
        unitDescriptionPlural,
      } = countryToValidate;
      const plan = plans[0] ?? {};
      const { usageUnitPrice, usageMinUnitPrice, usageMaxUnitPrice } = plan ?? {};

      // if (!`${trialUnits ?? '0'}`.match(floatNotNegativeNumberRegex)) {
      //   countryErrors.trialUnits = t('errors.invalidValue');
      // }
      if (unitDescription?.length === 0) {
        countryErrors.unitDescription = t('errors.required');
      }
      if (unitDescriptionPlural?.length === 0) {
        countryErrors.unitDescriptionPlural = t('errors.required');
      }
      if (
        !copyPrices &&
        !isVariablePPU &&
        !`${usageUnitPrice ?? ''}`.match(priceWithMax2DecimalsRegex)
      ) {
        // planErrors.usageUnitPrice = t('errors.price.invalid');
        countryErrors[`plans[0].usageUnitPrice`] = t('errors.price.invalid');
      }
      if (
        !copyPrices &&
        isVariablePPU &&
        !`${usageMinUnitPrice ?? ''}`.match(priceWithMax2DecimalsRegex)
      ) {
        // planErrors.usageMinUnitPrice = t('errors.price.invalid');
        countryErrors[`plans[0].usageMinUnitPrice`] = t('errors.price.invalid');
      }
      if (
        !copyPrices &&
        isVariablePPU &&
        !`${usageMaxUnitPrice ?? ''}`.match(priceWithMax2DecimalsRegex)
      ) {
        countryErrors[`plans[0].usageMaxUnitPrice`] = t('errors.price.invalid');
      }
      if (!!onetimeFee && !`${activationFeePrice ?? ''}`.match(priceWithMax2DecimalsRegex)) {
        countryErrors.activationFeePrice = t('errors.price.invalid');
      }
      if (!priceDescription?.length) {
        countryErrors.priceDescription = t('errors.required');
      }
      errors?.allowedCountries?.push(countryErrors);
    });
    // get all countries' currencies
    const allowedCountriesCurrencies = allowedCountries.reduce(
      (allCurrencies: [string], countryWithCurrency: IAppCountryForm) =>
        allCurrencies.includes(countryWithCurrency.currencyCode)
          ? allCurrencies
          : [...allCurrencies, countryWithCurrency.currencyCode],
      []
    );
    // validate if values are provided in copy prices inputs
    if (copyPrices) {
      if (isVariablePPU) {
        // variable pay per usage
        allowedCountriesCurrencies.forEach((currency: string) => {
          if (
            !(otherValues[`copyPricesMin${currency}`] ?? '')
              .toString()
              .match(priceWithMax2DecimalsRegex)
          ) {
            errors[`copyPricesMin${currency}`] = t('errors.price.invalid');
          }
          if (
            !(otherValues[`copyPricesMax${currency}`] ?? '')
              .toString()
              .match(priceWithMax2DecimalsRegex)
          ) {
            errors[`copyPricesMax${currency}`] = t('errors.price.invalid');
          }
        });
      }
      if (!isVariablePPU) {
        // fixed pay per usage
        allowedCountriesCurrencies.forEach((currency: string) => {
          if (
            !(otherValues[`copyPricesUnitPrice${currency}`] ?? '')
              .toString()
              .match(priceWithMax2DecimalsRegex)
          ) {
            errors[`copyPricesUnitPrice${currency}`] = t('errors.price.invalid');
          }
        });
      }
    }

    // return empty object if no errors
    const hasErrors =
      Object.keys(errors).length > 1 ||
      errors?.allowedCountries?.reduce(
        (hasError: boolean, countryErrObject: { [key: string]: string }) => {
          return hasError || Object.keys(countryErrObject).length > 0;
        },
        false
      );
    return hasErrors ? errors : {};
  }, []);

  // Free
  const freeTransform = useCallback((values) => {
    const { allowedCountries } = values;
    return allowedCountries.map((country: IAppCountryForm) => ({
      country: country?.country,
      activationFeePrice: 0,
      plans: [],
      trialUnits: 0,
      trialDays: 0,
      unitDescription: '',
      unitDescriptionPlural: '',
      priceDescription: '',
    }));
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      const { pricingType } = values;
      const isMembership = pricingType === 'membership';
      const isOnetime = pricingType === 'onetime';
      const isPayPerUsage = pricingType === 'payPerUsage';
      const isFree = pricingType === 'free';
      // build payload after validating
      let transformedValues;
      if (isOnetime) {
        const errors = onetimeValidate(values);
        if (Object.keys(errors).length > 0) return errors;
        transformedValues = onetimeTransform(values);
      }
      if (isMembership) {
        const errors = membershipValidate(values);
        if (Object.keys(errors).length > 0) return errors;
        transformedValues = membershipTransform(values);
      }
      if (isPayPerUsage) {
        const errors = payPerUsageValidate(values);
        if (Object.keys(errors).length > 0) return errors;
        transformedValues = payPerUsageTransform(values);
      }
      if (isFree) transformedValues = freeTransform(values);
      const data = await dispatchRequest(
        savePricingInfo({ allowedCountries: transformedValues, id })
      );
      if (data?.error) {
        if (data?.error?.data?.errors?.length) {
          return { [FORM_ERROR]: data.error.data.errors[0] };
        }
        if (data?.error) {
          return { [FORM_ERROR]: data.error };
        }
      }
    },
    [
      id,
      freeTransform,
      dispatchRequest,
      onetimeValidate,
      onetimeTransform,
      membershipValidate,
      payPerUsageValidate,
      membershipTransform,
      payPerUsageTransform,
    ]
  );

  return (
    <Form
      onSubmit={handleFormSubmit}
      mutators={{ ...arrayMutators }}
      initialValues={{
        status,
        copyPrices: false,
        membershipType: isMembershipMultiplePlans ? 'multiple' : 'single',
        payPerUsageType: isPayPerUsageFixed ? 'fixed' : 'variable', // depends on prices in plans
        pricingType,
        plans: transformedInitialPlans,
        allowedCountries: initialAllowedCountries,
      }}
      render={AppPricingForm}
    />
  );
};

export default AppPricing;
